import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  Calculator, 
  Users, 
  Target, 
  BarChart3,
  Settings,
  AlertCircle,
  Building,
  Layers,
  GitBranch,
  FileText,
  DollarSign,
  Archive,
  Factory,
  Scale
} from "lucide-react";
import ThreeFactorFormula from "./ThreeFactorFormula";
import IntermediateAllocations from "./IntermediateAllocations";
import BusinessUnitManager from "./BusinessUnitManager";
import AllocationPreview from "./AllocationPreview";
import SquareFootageManager from "./SquareFootageManager";
import CustomPoolDesigner from "./CustomPoolDesigner";
import AllocationFlowVisualizer from "./AllocationFlowVisualizer";
import IRDBPPoolManager from "./IRDBPPoolManager";
import COMCalculator from "./COMCalculator";
import ScheduleLNPreview from "./ScheduleLNPreview";
import ScheduleFPreview from "./ScheduleFPreview";
import ScheduleGReconciliation from "./ScheduleGReconciliation";

const AdvancedAllocations = ({ calculationId, filename, trialBalanceAccounts = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("three-factor");

  // State for data sharing between components
  const [businessUnits, setBusinessUnits] = useState([]);
  const [threeFactorFormula, setThreeFactorFormula] = useState(null);
  const [intermediatePools, setIntermediatePools] = useState([]);
  const [allocationResults, setAllocationResults] = useState([]);
  
  // Phase 2B state additions
  const [squareFootageLocations, setSquareFootageLocations] = useState([]);
  const [customPools, setCustomPools] = useState([]);
  const [squareFootageResults, setSquareFootageResults] = useState([]);
  const [customPoolResults, setCustomPoolResults] = useState([]);

  // Advanced allocation state additions
  const [irdPools, setIrdPools] = useState([]);
  const [bpPools, setBpPools] = useState([]);
  const [comAssets, setComAssets] = useState([]);
  const [comCalculations, setComCalculations] = useState([]);
  const [scheduleLNData, setScheduleLNData] = useState({});

  // Callback functions for data updates
  const handleBusinessUnitsUpdate = (units) => {
    setBusinessUnits(units);
  };

  const handleThreeFactorUpdate = (formula) => {
    setThreeFactorFormula(formula);
  };

  const handleIntermediatePoolsUpdate = (pools) => {
    setIntermediatePools(pools);
  };

  const handleAllocationResultsUpdate = (results) => {
    setAllocationResults(results);
  };

  // Phase 2B callback functions
  const handleSquareFootageLocationsUpdate = (locations) => {
    setSquareFootageLocations(locations);
  };

  const handleSquareFootageAllocationsUpdate = (results) => {
    setSquareFootageResults(results);
  };

  const handleCustomPoolsUpdate = (pools) => {
    setCustomPools(pools);
  };

  const handleCustomPoolResultsUpdate = (results) => {
    setCustomPoolResults(results);
  };

  // Advanced callback functions
  const handleIRDPoolsUpdate = (pools) => {
    setIrdPools(pools);
  };

  const handleBPPoolsUpdate = (pools) => {
    setBpPools(pools);
  };

  const handleCOMAssetsUpdate = (assets) => {
    setComAssets(assets);
  };

  const handleCOMCalculationsUpdate = (calculations) => {
    setComCalculations(calculations);
  };

  const handleScheduleLNUpdate = (schedules) => {
    setScheduleLNData(schedules);
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="h-5 w-5" />
            <span>Advanced Allocations</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading advanced allocations...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="h-5 w-5" />
            <span>Advanced Allocations</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-red-700">
              {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calculator className="h-5 w-5 text-indigo-600" />
          <span>Advanced Allocations</span>
          <Badge variant="secondary">{filename}</Badge>
        </CardTitle>
        <div className="text-sm text-slate-600 mt-2">
          Complete allocation management: Multi-tier allocations, facility distributions, custom pools, IR&D/B&P management, Cost of Money calculations, and DCAA allocation summaries
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
          <TabsList className="grid w-full grid-cols-11 mb-6 gap-1">
            <TabsTrigger value="three-factor" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Target className="h-3 w-3" />
              <span>Three-Factor</span>
            </TabsTrigger>
            <TabsTrigger value="intermediate" className="flex items-center space-x-1 text-xs px-1 py-2">
              <BarChart3 className="h-3 w-3" />
              <span>Intermediate</span>
            </TabsTrigger>
            <TabsTrigger value="square-footage" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Building className="h-3 w-3" />
              <span>Sq. Footage</span>
            </TabsTrigger>
            <TabsTrigger value="custom-pools" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Layers className="h-3 w-3" />
              <span>Custom Pools</span>
            </TabsTrigger>
            <TabsTrigger value="ird-bp" className="flex items-center space-x-1 text-xs px-1 py-2">
              <FileText className="h-3 w-3" />
              <span>IR&D/B&P</span>
            </TabsTrigger>
            <TabsTrigger value="com-calculator" className="flex items-center space-x-1 text-xs px-1 py-2">
              <DollarSign className="h-3 w-3" />
              <span>COM</span>
            </TabsTrigger>
            <TabsTrigger value="schedules-ln" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Archive className="h-3 w-3" />
              <span>DCAA</span>
            </TabsTrigger>
            <TabsTrigger value="schedule-f" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Factory className="h-3 w-3" />
              <span>Schedule F</span>
            </TabsTrigger>
            <TabsTrigger value="schedule-g" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Scale className="h-3 w-3" />
              <span>Schedule G</span>
            </TabsTrigger>
            <TabsTrigger value="business-units" className="flex items-center space-x-1 text-xs px-1 py-2">
              <Users className="h-3 w-3" />
              <span>Bus. Units</span>
            </TabsTrigger>
            <TabsTrigger value="flow-visualizer" className="flex items-center space-x-1 text-xs px-1 py-2">
              <GitBranch className="h-3 w-3" />
              <span>Flow Visual</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="three-factor" className="space-y-6">
            <ThreeFactorFormula 
              calculationId={calculationId}
              businessUnits={businessUnits}
              onFormulaUpdate={handleThreeFactorUpdate}
              onResultsUpdate={handleAllocationResultsUpdate}
            />
          </TabsContent>

          <TabsContent value="intermediate" className="space-y-6">
            <IntermediateAllocations 
              calculationId={calculationId}
              onPoolsUpdate={handleIntermediatePoolsUpdate}
              onResultsUpdate={handleAllocationResultsUpdate}
            />
          </TabsContent>

          <TabsContent value="square-footage" className="space-y-6">
            <SquareFootageManager
              calculationId={calculationId}
              onLocationsUpdate={handleSquareFootageLocationsUpdate}
              onAllocationsUpdate={handleSquareFootageAllocationsUpdate}
            />
          </TabsContent>

          <TabsContent value="custom-pools" className="space-y-6">
            <CustomPoolDesigner
              calculationId={calculationId}
              businessUnits={businessUnits}
              onPoolsUpdate={handleCustomPoolsUpdate}
              onResultsUpdate={handleCustomPoolResultsUpdate}
            />
          </TabsContent>

          <TabsContent value="ird-bp" className="space-y-6">
            <IRDBPPoolManager
              key={`irdbp-${Date.now()}`}
              calculationId={calculationId}
              trialBalanceAccounts={trialBalanceAccounts}
              onIRDPoolsUpdate={handleIRDPoolsUpdate}
              onBPPoolsUpdate={handleBPPoolsUpdate}
            />
          </TabsContent>

          <TabsContent value="com-calculator" className="space-y-6">
            <COMCalculator
              calculationId={calculationId}
              squareFootageLocations={squareFootageLocations}
              onAssetsUpdate={handleCOMAssetsUpdate}
              onCalculationsUpdate={handleCOMCalculationsUpdate}
            />
          </TabsContent>

          <TabsContent value="schedules-ln" className="space-y-6">
            <ScheduleLNPreview
              calculationId={calculationId}
              onSchedulesUpdate={handleScheduleLNUpdate}
            />
          </TabsContent>

          <TabsContent value="business-units" className="space-y-6">
            <BusinessUnitManager 
              calculationId={calculationId}
              onUnitsUpdate={handleBusinessUnitsUpdate}
            />
          </TabsContent>

          <TabsContent value="flow-visualizer" className="space-y-6">
            <AllocationFlowVisualizer
              calculationId={calculationId}
              businessUnits={businessUnits}
              threeFactorFormula={threeFactorFormula}
              intermediatePools={intermediatePools}
              squareFootageResults={squareFootageResults}
              customPoolResults={customPoolResults}
              allocationResults={allocationResults}
            />
          </TabsContent>

          <TabsContent value="schedule-f" className="space-y-6">
            <ScheduleFPreview
              calculationId={calculationId}
            />
          </TabsContent>

          <TabsContent value="schedule-g" className="space-y-6">
            <ScheduleGReconciliation
              calculationId={calculationId}
            />
          </TabsContent>
        </Tabs>

        {/* Enhanced Information Panel */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-slate-600">
            <AlertCircle className="h-4 w-4" />
            <div>
              <p className="font-medium">Advanced Allocations & DCAA Summaries</p>
              <ul className="text-xs mt-1 space-y-1">
                <li>• <strong>Core Allocations:</strong> Three-Factor Formula, Intermediate Pools, Square Footage, and Custom Pool management</li>
                <li>• <strong>Advanced Features:</strong> IR&D/B&P Pool management with account tagging and DCAA compliance tracking</li>
                <li>• <strong>Cost of Money:</strong> Comprehensive COM Calculator with Net Book Value calculations and asset management</li>
                <li>• <strong>DCAA Summaries:</strong> Automated DCAA-compliant summaries for IR&D/B&P Pools, Allocation Types, and Facilities Capital Cost of Money</li>
                <li>• <strong>Complete Integration:</strong> All allocation types feed into variance monitoring and comprehensive flow visualization</li>
                <li>• <strong>DCAA Compliance:</strong> Full audit trail, allowability tracking, and submission-ready schedule formatting</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdvancedAllocations;